<script lang="ts" setup>
import MIcon from "../../medicus/MIcon";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useEvent } from "vue-composable";
import { showDialog } from "../../../utils/dialog";
import { pusher } from "../../../plugins/thirdparties/pusher";
import { useAuth } from "../../../store";
import { useSnackbar } from "../../../composables/snackbar";
import axios from "../../../utils/axios";
import MLayoutStack from "../../medicus/MLayoutStack/MLayoutStack.vue";
import { useDrawerStore } from "../../../store/drawers";
import { dispatch } from "../../../utils/bus";
import { useRoute } from "vue-router";

defineProps<{ opened?: Boolean }>();

useEvent(window, "keydown", (ev) => {
  // Ctrl/cmd + shift + C
  if (ev.key !== "c") return;
  if (!ev.ctrlKey && !ev.metaKey) return;
  if (!ev.shiftKey) return;
  ev.preventDefault();
  if (openDialog) return;
  openSearch();
});

let openDialog: ReturnType<typeof showDialog> | null = null;

function openSearch() {
  useDrawerStore().open("modal", `/chat/chat-finder`, {});
}

const isUnread = ref<Boolean>(false);
// ENG-21233 no top-level await: keeps this a sync component so it renders with
// its siblings on first paint instead of popping in once the request resolves
axios.get(`/chat/has-unread`).then((resp) => {
  isUnread.value = resp.data.unreadCount > 0;
});

interface ChatPusherEvent {
  id: string;
  title?: string;
  type?: string;
}

const broadcastChannels: ReturnType<typeof pusher.subscribe>[] = [];

const route = useRoute();

onMounted(() => {
  const store = useAuth();

  const channelName = store.config?.tenantId + "-chatparticipant-" + store.config?.staff.id;
  let channel = pusher.subscribe(channelName);

  channel.bind("all_messages_read", () => {
    isUnread.value = false;
  });
  channel.bind("chat_updated", (v: ChatPusherEvent) => {
    isUnread.value = true;
    // if we happen to be viewing the chat dont show the toast
    if (v.id !== route.query?.id) {
      useSnackbar().add({
        title: "New Message",
        message: v.title,
        actions: [
          {
            label: "Open",
            to: `/chat/${v.type}/list?id=${v.id}`,
          },
        ],
        type: "system",
        timeout: 5000,
      });
    }
    console.log("chat_updated_via_pusher", v.id);
    dispatch("chatUpdated", v);
  });
  channel.bind("message_updated", (v: ChatPusherEvent) => {
    dispatch("messageUpdated", v);
  });

  broadcastChannels.push(channel);
});

onBeforeUnmount(() => {
  if (broadcastChannels.length === 0) {
    return;
  }

  broadcastChannels.forEach((x) => {
    x.unbind_all();
    x.unsubscribe();
  });
});
</script>

<template>
  <m-button
    :sr-label="!opened ? 'Go to Chat' : undefined"
    flat
    dense
    data-testid="go-to-chat-button"
    @click="openSearch"
  >
    <MLayoutStack horizontal center full class="relative-position">
      <m-icon name="fa-regular fa-comment" />
      <span>Chat</span>
      <!-- ENG-21283 CSS dot, not an <i>/.m-icon glyph: the sidebar forces
           `.medicus-button-container i { font-size: 18px !important }`, which
           overrode fa-xs and blew the dot up to icon size -->
      <span v-if="isUnread" class="green-icon" aria-hidden="true" />
    </MLayoutStack>
  </m-button>
</template>
<style lang="scss">
.sidebar-expanded .green-icon {
  left: var(--space-3);
}

.modal-wrapper .app-chat-modal.modal {
  background-color: var(--surface-default);

  > .modal-header {
    box-shadow: none;
    padding: var(--space-2-5) var(--space-5) 0px var(--space-5);

    > .modal-title {
      font-family: Arial, sans-serif;
      font-style: normal;
      font-weight: 700;
      font-size: var(--font-size-xl);
      line-height: 100%;

      margin: auto 0;
    }
  }
}

.green-icon {
  position: absolute;
  left: var(--space-5);
  top: -2px;
  width: 10px;
  height: 10px;
  border-radius: var(--radius-circle);
  background-color: var(--status-green);
}
</style>
